import React, { useState, useRef } from 'react';
import { tWithKeyAttr as t } from "../../../i18nHelper";
import Modal from "../modal/Modal";
import useAutosizeTextArea from "../../../hooks/useAutosizeTextarea";
import { useUpdateClinicalSummaryWithTherapistResponseMutation } from "../../../services/api/ai.api";
import { DEFAULT_LANGUAGE } from "../../../utils/constants";
import i18n from "../../../i18n";
import styles from "./clinicalBackgroundCommentModal.module.css";

interface ClinicalBackgroundCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userGuid?: string;
}

const ClinicalBackgroundCommentModal: React.FC<ClinicalBackgroundCommentModalProps> = ({
  isOpen,
  onClose,
  userGuid,
}) => {
  const [comment, setComment] = useState('');
  const [updateClinicalSummary, { isLoading: isUpdating }] = useUpdateClinicalSummaryWithTherapistResponseMutation();

  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(commentTextareaRef.current, comment);

  const handleSave = async () => {
    if (!userGuid || !comment.trim()) {
      console.warn('Cannot save comment: userGuid or comment is missing');
      return;
    }
    
    try {
      await updateClinicalSummary({
        userGuid,
        therapistResponse: comment.trim(),
        language: i18n.language || DEFAULT_LANGUAGE,
      }).unwrap();
      
      onClose();
      setComment('');
    } catch (error) {
      console.error('Failed to save comment:', error);
    }
  };

  const handleCancel = () => {
    onClose();
    setComment('');
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={handleCancel} wide={true}>
      <div className={styles.modal}>
        <h2 className={styles.modalTitle}>{t('ADD_COMMENT_1')}</h2>
        <label htmlFor="comment-textarea" className={styles.modalLabel}>{t('RESPONSE')}</label>
        <textarea
          id="comment-textarea"
          ref={commentTextareaRef}
          className={styles.modalTextarea}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('PRE_RESPONSE_TEXT')}
          rows={4}
        />
        <div className={styles.modalActions}>
          <button 
            className={styles.modalSave} 
            onClick={handleSave}
            disabled={isUpdating || !comment.trim()}
          >
            {isUpdating ? t('SAVING') || t('SAVE') : t('SAVE')}
          </button>
          <button 
            className={styles.modalCancel} 
            onClick={handleCancel}
            disabled={isUpdating}
          >
            {t('CANCEL')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ClinicalBackgroundCommentModal;

